<template>
	<div
		class="tabs"
		:class="{ 'tabs--fixed': hasFixedHeight }"
		:data-event-type="attributes?.type ?? ''"
	>
		<ul v-if="tabs.length > 1" class="tabs__list">
			<li
				class="tabs__list-item"
				:class="{
					'tabs__list-item--active': selectedTab.name === tab.name,
					'tabs__list-item--icon': !!tab.icon,
				}"
				v-for="(tab, index) in tabs"
				:key="index"
				@click="selectTab(index)"
			>
				<span
					v-if="tab.icon"
					class="tabs__list-item-icon"
					:class="[
						`icon-${tab.icon}${selectedTab.name === tab.name ? '-active' : ''}-56`,
					]"
				/>
				<ShrinkText
					v-else
					:text="translate(tab.name)"
					:fontSize="16"
					:forcedFontSize="shrinkMinFontSize"
					isBold
					:containerClassList="`tabs__list-item-name ${tab.showEventIcon ? `${attributes.type}-event-icon` : ''}`"
				/>
				<slot :name="`listSlot${index + 1}`" />
				<AppNotification
					v-if="tab.hasNotification"
					class="tabs__list-item-notification"
					:type="StatusTypes.WARNING"
					:value="
						!!tab.notificationCount ? tab.notificationCount : '!'
					"
				/>
				<div
					v-if="showSeparator(index)"
					class="tabs__list-item-separator"
				/>
			</li>
		</ul>
		<ScrollFade :key="redrawIteration" isTabsInterface>
			<template #scrollContent>
				<div class="tabs__content">
					<slot
						:name="`tab${tabs.findIndex((tab: Tab): boolean => tab.name === selectedTab.name) + 1}`"
					/>
				</div>
			</template>
		</ScrollFade>
		<div
			:id="`tab${tabs.findIndex((tab: Tab): boolean => tab.name === selectedTab.name) + 1}-no-scroll`"
			class="tab-no-scroll"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppNotification from "@/components/AppNotification.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import { useEventStore } from "@/event";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import type { Tab } from "@/types/ui";
import { routesWithFixedModalHeight, StatusTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import {
	computed,
	markRaw,
	onBeforeUnmount,
	onMounted,
	ref,
	toRefs,
	watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";

const router = useRouter();
const route = useRoute();

const eventStore = useEventStore();
const { attributes } = storeToRefs(eventStore);

const emitter = markRaw(useEmitter());

const soundService = markRaw(SoundService.getInstance());

interface Props {
	tabs: Array<Tab>;
	selectedIndex?: number | null;
}

const props = defineProps<Props>();
const { tabs, selectedIndex } = toRefs(props);

const selectedTab = ref<Tab>(tabs.value[0]);
const emit = defineEmits(["tabSelected"]);

const showSeparator = (index: number): boolean => {
	return (
		// the current index is not the selected/active tab
		!selectedTab.value.name.includes(tabs.value[index].name) &&
		// current index is not the last one
		index !== tabs.value.length - 1 &&
		// the tab at the next index is not the currently selected/active one
		// because the separator is positioned to the right of the current tab
		!(selectedTab.value.name === tabs.value[index + 1].name)
	);
};

onMounted((): void => {
	if (!selectedIndex?.value) return;

	selectedTab.value = tabs.value[selectedIndex.value];

	router.replace({
		name: route.name,
		params: {
			...route.params, // Preserves all existing parameters
			tab: undefined, // Declaratively overwrite/remove only 'tab'
		},
	});
});

watch(
	(): number | null | undefined => selectedIndex?.value,
	(): void => {
		if (!selectedIndex?.value) return;

		selectedTab.value = tabs.value[selectedIndex.value];
		router.replace({
			name: route.name,
			params: {
				...route.params,
				tab: undefined,
			},
		});
	},
);

const redrawIteration = ref<number>(0);
watch(
	(): Tab => selectedTab.value,
	(): void => {
		redrawIteration.value += 1;
		emitter.emit(EmitterEvents.SwitchPopupTab);
	},
);

const hasFixedHeight = computed((): boolean => {
	return Object.keys(routesWithFixedModalHeight).some(
		key => route.name === key,
	);
});

const selectTab = (index: number): void => {
	soundService.playGenerickClick();
	selectedTab.value = tabs.value[index];
	emit("tabSelected", index);
};

const timeout = ref<ReturnType<typeof setTimeout> | null>(null);

// if null, the font size will be dynamically calculated for each item
// otherwise, a uniform font size will be enforced
const shrinkMinFontSize = ref<number | null>(null);

const handleShrinkText = (textContainer: HTMLElement): void => {
	timeout.value = setTimeout((): void => {
		const textElements =
			textContainer.getElementsByClassName("shrink-text");

		if (textElements.length === 0) {
			return;
		}

		const minFontSize = Array.from(textElements).reduce(
			(minFontSize: number, element: Element): number => {
				const fontSize = parseInt(
					window.getComputedStyle(element).fontSize,
					10,
				);
				return Math.min(minFontSize, fontSize);
			},
			Infinity,
		);

		shrinkMinFontSize.value = minFontSize;
	}, 50);
};

onMounted((): void => {
	// TODO template ref
	const textContainer = document.querySelector(".tabs__list");

	if (textContainer) {
		handleShrinkText(textContainer as HTMLElement);
	}
});

onBeforeUnmount((): void => {
	if (timeout.value) {
		clearTimeout(timeout.value);
	}
});
</script>

<style lang="scss" scoped>
.tabs {
	@include flex-col-start;
	height: 100%;

	&--event {
		.tabs__list-item {
			background-image: linear-gradient(
				0deg,
				rgba(57, 10, 11, 1) 0%,
				rgba(71, 10, 12, 1) 100%
			);

			&-separator {
				background-image: linear-gradient(
					to top,
					rgba(160, 16, 26, 0.7),
					rgba(160, 16, 26, 0.5) 90%,
					rgb(205, 104, 107, 0.5)
				);
			}

			&--active {
				background-image: linear-gradient(
					to top,
					rgba(160, 16, 26, 0.7),
					rgba(160, 16, 26, 0.5) 90%,
					rgba(205, 104, 107, 0.5)
				);
			}
		}
		:deep(.scroll-fade-container--tabs) {
			background-image: linear-gradient(
				0deg,
				rgba(17, 1, 4, 0.7) 0%,
				rgba(160, 16, 26, 0.7) 100%
			);
		}
	}

	&__list {
		width: 100%;
		@include flex-row-center;
		list-style-type: none;

		&-item {
			position: relative;
			flex: 1 1 0;
			cursor: pointer;
			@include flex-row-center;
			padding: 0 0.5rem; // NOTE: needed for ShrinkText, so it doesn't incorrectly detect the available space for text
			min-width: 0;
			height: 2.188rem;

			&-name {
				display: flex;
				padding-bottom: 0.25rem;
				width: 100%; // this is important
				color: #f2dcb5;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				text-transform: uppercase;

				:deep(.v-popper) {
					display: flex;
					width: 100%;
				}
				&.christmas-event-icon::before {
					margin-right: 0.4rem;
					width: 1rem;
					height: 1rem;
					content: "";
					@include background(
						url($path-events + "christmas/icons/toy-beige-40.avif"),
						contain,
						center,
						no-repeat
					);
				}
				&.easter-event-icon::before {
					margin-right: 0.4rem;
					width: 1rem;
					height: 1rem;
					content: "";
					@include background(
						url($path-events + "easter/icons/ico-rabbit-beige-40.avif"),
						contain,
						center,
						no-repeat
					);
				}
				&.fishing-event-icon::before {
					margin-right: 0.4rem;
					width: 1rem;
					height: 1rem;
					content: "";
					@include background(
						url($path-events + "fishing/icons/ico-hook-beige-40.avif"),
						contain,
						center,
						no-repeat
					);
				}
				&.firefest-event-icon::before {
					margin-right: 0.4rem;
					width: 1rem;
					height: 1rem;
					content: "";
					@include background(
						url($path-events + "firefest/icons/ico-fire-beige-40.avif"),
						contain,
						center,
						no-repeat
					);
				}
				&.solarfair-event-icon::before {
					margin-right: 0.4rem;
					width: 1rem;
					height: 1rem;
					content: "";
					@include background(
						url($path-events + "solarfair/icons/ico-sun-beige-40.avif"),
						contain,
						center,
						no-repeat
					);
				}
				&.hunting-event-icon::before {
					margin-right: 0.4rem;
					width: 1rem;
					height: 1rem;
					content: "";
					@include background(
						url($path-events + "hunting/icons/ico_bow-and-arrow-beige-40.avif"),
						contain,
						center,
						no-repeat
					);
				}
				&.halloween-event-icon::before {
					margin-right: 0.4rem;
					width: 1rem;
					height: 1rem;
					content: "";
					@include background(
						url($path-events + "halloween/icons/ico-pumpkin-beige-40.avif"),
						contain,
						center,
						no-repeat
					);
				}
				&.pirates-event-icon::before {
					margin-right: 0.4rem;
					width: 1rem;
					height: 1rem;
					content: "";
					@include background(
						url($path-events + "pirates/icons/ico-skull-beige-40.avif"),
						contain,
						center,
						no-repeat
					);
				}
			}

			&-notification {
				position: absolute;
				top: 0.1rem;
				right: 0.1rem;
			}

			&--active {
				background-image: linear-gradient(
					to top,
					rgba(60, 61, 67, 0.5),
					rgba(60, 61, 67, 0.5) 90%,
					rgb(105, 106, 114, 0.5)
				);

				.tabs__list-item-name {
					color: #f4d278;
					&.christmas-event-icon::before {
						@include background(
							url($path-events + "christmas/icons/toy-gold-40.avif"),
							contain,
							center,
							no-repeat
						);
					}
					&.easter-event-icon::before {
						@include background(
							url($path-events + "easter/icons/ico-rabbit-gold-40.avif"),
							contain,
							center,
							no-repeat
						);
					}
					&.fishing-event-icon::before {
						@include background(
							url($path-events + "fishing/icons/ico-hook-gold-40.avif"),
							contain,
							center,
							no-repeat
						);
					}
					&.firefest-event-icon::before {
						@include background(
							url($path-events + "firefest/icons/ico-fire-gold-40.avif"),
							contain,
							center,
							no-repeat
						);
					}
					&.solarfair-event-icon::before {
						@include background(
							url($path-events + "solarfair/icons/ico-sun-gold-40.avif"),
							contain,
							center,
							no-repeat
						);
					}
					&.hunting-event-icon::before {
						@include background(
							url($path-events + "hunting/icons/ico_bow-and-arrow-gold-40.avif"),
							contain,
							center,
							no-repeat
						);
					}
					&.halloween-event-icon::before {
						@include background(
							url($path-events + "halloween/icons/ico-pumpkin-gold-40.avif"),
							contain,
							center,
							no-repeat
						);
					}
					&.pirates-event-icon::before {
						@include background(
							url($path-events + "pirates/icons/ico-skull-gold-40.avif"),
							contain,
							center,
							no-repeat
						);
					}
				}
			}
			&--icon {
				height: 3.625rem;
			}

			&-separator {
				position: absolute;
				top: 0;
				right: -0.063rem;
				background-image: linear-gradient(
					to top,
					rgba(60, 61, 67, 0.5),
					rgba(60, 61, 67, 0.5) 90%,
					rgba(105, 106, 114, 0.5)
				);
				width: 0.125rem;
				height: 100%;
			}
		}
	}

	.scroll-fade-container {
		// very important
		display: flex;
		flex: 1 1 0;
		width: 100%;
		height: 100%;
	}

	&--fixed {
		.scroll-fade-container {
			min-height: 0;
		}
	}

	&__content {
		flex: 1; // NOTE: supports height for scroll-fade-container fill
		padding: 0.125rem 0 0.5rem 0; // universal normalization adjustment for positional content padding
		@include flex-col-start;
		width: 100%;
	}
	&[data-event-type="christmas"] {
		&.tabs--event {
			.tabs__list-item {
				background-image: linear-gradient(
					0deg,
					rgba(57, 10, 11, 1) 0%,
					rgba(71, 10, 12, 1) 100%
				);

				&-separator {
					background-image: linear-gradient(
						to top,
						rgba(160, 16, 26, 0.7),
						rgba(160, 16, 26, 0.5) 90%,
						rgb(205, 104, 107, 0.5)
					);
				}

				&--active {
					background-image: linear-gradient(
						to top,
						rgba(160, 16, 26, 0.7),
						rgba(160, 16, 26, 0.5) 90%,
						rgba(205, 104, 107, 0.5)
					);
				}
			}
			:deep(.scroll-fade-container--tabs) {
				background-image: linear-gradient(
					0deg,
					rgba(17, 1, 4, 0.7) 0%,
					rgba(160, 16, 26, 0.7) 100%
				);
			}
		}
	}
	&[data-event-type="easter"] {
		&.tabs--event {
			.tabs__list-item {
				background: linear-gradient(
					0deg,
					rgba(24, 33, 17, 1) 0%,
					rgba(42, 55, 31, 1) 100%
				);

				&-separator {
					background-image: linear-gradient(
						0deg,
						rgba(57, 54, 10, 0.3) 0%,
						rgba(146, 139, 24, 0.3) 100%
					);
				}

				&--active {
					background: linear-gradient(
						0deg,
						rgba(146, 139, 24, 0.3) 0%,
						rgba(146, 139, 24, 0.3) 85%,
						rgba(203, 197, 100, 0.3) 100%
					);
				}
			}
			:deep(.scroll-fade-container--tabs) {
				background-image: linear-gradient(
					0deg,
					rgba(57, 54, 10, 0.3) 0%,
					rgba(146, 139, 24, 0.3) 100%
				);
			}
		}
	}
	&[data-event-type="fishing"] {
		&.tabs--event {
			.tabs__list-item {
				background: linear-gradient(
					0deg,
					rgba(10, 44, 56, 1) 0%,
					rgba(10, 55, 71, 1) 100%
				);

				&-separator {
					background-image: linear-gradient(
						0deg,
						rgba(16, 122, 161, 0.3) 0%,
						rgba(16, 122, 161, 0.5) 85%,
						rgba(104, 177, 204, 0.5) 100%
					);
				}

				&--active {
					background: linear-gradient(
						0deg,
						rgba(16, 122, 161, 0.3) 0%,
						rgba(16, 122, 161, 0.5) 85%,
						rgba(104, 177, 204, 0.5) 100%
					);
				}
			}
			:deep(.scroll-fade-container--tabs) {
				background-image: linear-gradient(
					0deg,
					rgba(5, 48, 63, 0.3) 0%,
					rgba(16, 122, 161, 0.3) 100%
				);
			}
		}
	}
	&[data-event-type="firefest"] {
		&.tabs--event {
			.tabs__list-item {
				background: linear-gradient(
					0deg,
					rgba(67, 24, 13, 1) 25%,
					rgba(89, 27, 10, 1) 75%
				);
				&-separator {
					background-image: linear-gradient(
						0deg,
						rgba(203, 56, 17, 0.6) 0%,
						rgba(203, 56, 17, 0.5) 85%,
						rgba(205, 123, 101, 0.5) 100%
					);
				}

				&--active {
					background: linear-gradient(
						0deg,
						rgba(203, 56, 17, 0.6) 0%,
						rgba(203, 56, 17, 0.5) 85%,
						rgba(205, 123, 101, 0.5) 100%
					);
				}
			}
			:deep(.scroll-fade-container--tabs) {
				background-image: linear-gradient(
					0deg,
					rgba(44, 16, 9, 0.6) 25%,
					rgba(203, 56, 17, 0.6) 75%
				);
			}
		}
	}
	&[data-event-type="solarfair"] {
		&.tabs--event {
			.tabs__list-item {
				background: linear-gradient(
					0deg,
					rgba(122, 49, 2, 1) 0%,
					rgba(118, 45, 2, 1) 100%
				);
				&-separator {
					background: linear-gradient(
						0deg,
						rgba(142, 82, 12, 1) 0%,
						rgba(163, 106, 22, 1) 100%
					);
				}

				&--active {
					background: linear-gradient(
						0deg,
						rgba(142, 82, 12, 1) 0%,
						rgba(163, 106, 22, 1) 100%
					);
				}
			}
			:deep(.scroll-fade-container--tabs) {
				background-image: linear-gradient(
					0deg,
					rgba(67, 24, 13, 1) 0%,
					rgba(142, 82, 12, 1) 100%
				);
			}
		}
	}
	&[data-event-type="hunting"] {
		&.tabs--event {
			.tabs__list-item {
				background: linear-gradient(
					0deg,
					rgba(8, 36, 3, 1) 0%,
					rgba(12, 49, 5, 1) 100%
				);

				&-separator {
					background-image: linear-gradient(
						0deg,
						rgba(22, 101, 26, 0.3) 0%,
						rgba(22, 101, 26, 0.3) 75%,
						rgba(60, 147, 64, 0.3) 100%
					);
				}

				&--active {
					background: linear-gradient(
						0deg,
						rgba(22, 101, 26, 0.3) 0%,
						rgba(22, 101, 26, 0.3) 75%,
						rgba(60, 147, 64, 0.3) 100%
					);
				}
			}
			:deep(.scroll-fade-container--tabs) {
				background-image: linear-gradient(
					0deg,
					rgba(5, 51, 7, 0.3) 0%,
					rgba(22, 101, 26, 0.3) 100%
				);
			}
		}
	}
	&[data-event-type="halloween"] {
		&.tabs--event {
			.tabs__list-item {
				background: linear-gradient(
					0deg,
					rgba(22, 17, 33, 1) 25%,
					rgba(46, 36, 69, 1) 75%
				);

				&-separator {
					background-image: linear-gradient(
						0deg,
						rgba(66, 37, 133, 0.3) 0%,
						rgba(66, 37, 133, 0.3) 85%,
						rgba(131, 100, 204, 0.3) 100%
					);
				}

				&--active {
					background: linear-gradient(
						0deg,
						rgba(66, 37, 133, 0.3) 0%,
						rgba(66, 37, 133, 0.3) 85%,
						rgba(131, 100, 204, 0.3) 100%
					);
				}
			}
			:deep(.scroll-fade-container--tabs) {
				background-image: linear-gradient(
					0deg,
					rgba(24, 10, 56, 0.3) 0%,
					rgba(66, 37, 133, 0.3) 100%
				);
			}
		}
	}
	&[data-event-type="pirates"] {
		&.tabs--event {
			.tabs__list-item {
				background: linear-gradient(
					0deg,
					rgba(10, 28, 48, 1) 0%,
					rgba(20, 50, 85, 1) 100%
				);

				&-separator {
					background-image: linear-gradient(
						0deg,
						rgba(15, 76, 144, 0.3) 0%,
						rgba(15, 76, 144, 0.5) 85%,
						rgba(103, 141, 182, 0.5) 100%
					);
				}

				&--active {
					background: linear-gradient(
						0deg,
						rgba(15, 76, 144, 0.3) 0%,
						rgba(15, 76, 144, 0.5) 85%,
						rgba(103, 141, 182, 0.5) 100%
					);
				}
			}
			:deep(.scroll-fade-container--tabs) {
				background-image: linear-gradient(
					0deg,
					rgba(8, 31, 59, 0.3) 0%,
					rgba(15, 76, 144, 0.3) 100%
				);
			}
		}
	}
}

.tab-no-scroll {
	padding: 0 0.5rem;
	width: 100%;
}
</style>
